import { useEffect, useRef } from 'react'

type Options = {
  hasMore: boolean
  isLoading: boolean
  onLoadMore: () => void
  rootMargin?: string
}

/**
 * Возвращает ref на элемент-маркер в конце списка: как только он попадает
 * в область видимости, вызывается onLoadMore.
 *
 * Колбэк хранится в ref, поэтому наблюдатель создаётся один раз, но всегда
 * видит актуальные hasMore/isLoading. Никакого дополнительного «замка» нет
 * намеренно: залипший флаг намертво убивает подгрузку, а лишний запрос в узком
 * окне до перерисовки безвреден — модель схлопывает дубли по id.
 */
export const useInfiniteScroll = <T extends HTMLElement = HTMLDivElement>({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = '200px',
}: Options) => {
  const sentinelRef = useRef<T>(null)
  const loadMoreRef = useRef<() => void>(() => undefined)

  useEffect(() => {
    loadMoreRef.current = () => {
      if (isLoading || !hasMore) {
        return
      }

      onLoadMore()
    }
  })

  useEffect(() => {
    const node = sentinelRef.current

    if (!node) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          loadMoreRef.current()
        }
      },
      { rootMargin },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [rootMargin])

  return sentinelRef
}
